import { useFileSelect } from "@jugl-web/domain-resources/files/hooks/useFileSelect";
import { CropImageModal } from "@jugl-web/ui-kit";
import { isFileTypeAccepted, useToast, useTranslations } from "@jugl-web/utils";
import { FC, useCallback, useState } from "react";
import { ChatBotImagePicker } from "../ChatBotImagePicker";

interface ChatBotChatAvatarPickerProps {
  image: string | File | null;
  onImageChange: (image: string | File | null) => void;
}

export const ChatBotChatAvatarPicker: FC<ChatBotChatAvatarPickerProps> = ({
  image,
  onImageChange,
}) => {
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | undefined>(undefined);

  const { selectFile } = useFileSelect();
  const { toast } = useToast();
  const { t } = useTranslations();

  const handleFileSelect = useCallback(async () => {
    const selectedFiles = await selectFile({
      acceptTypes: "image/*",
      maxFileSize: 1048576,
      multiple: false,
    });

    const file = selectedFiles?.[0];

    if (file) {
      if (!isFileTypeAccepted(file, "image/*")) {
        toast(
          t({
            id: "cx-ai-agents-page.incorrect-file-format",
            defaultMessage: "Incorrect file format ❌",
          }),
          { variant: "error" }
        );
        return;
      }

      setPendingFile(file);
      setIsCropModalOpen(true);
    }
  }, [selectFile, t, toast]);

  return (
    <>
      <ChatBotImagePicker
        image={image}
        onImageChange={handleFileSelect}
        onImageRemove={() => onImageChange(null)}
      />
      <CropImageModal
        isOpen={isCropModalOpen}
        image={pendingFile}
        onClose={() => setIsCropModalOpen(false)}
        onChangeImage={handleFileSelect}
        onSave={(croppedFile) => {
          onImageChange(croppedFile);
          setIsCropModalOpen(false);
        }}
      />
    </>
  );
};
